import { Container, Graphics, Sprite } from '@pixi/react'
import { useEffect, useRef, useState } from 'react'
import { convertPathToInstructions } from '../svg-utils'
import { calculateRelativePosition, calculateSizeOfPart } from '../utils'
import { usePartTable } from '../../hooks/PartTableHooks'
import { useAtom } from 'jotai'
import { renderAnchorsAtom } from '../../atoms/ViewOptions.atom'

const getColor = (node) => {
    const { graphical, hovered } = node

    if (hovered) {
        return 0x00ff00
    } else if (graphical) {
        return 0x0000ff
    } else {
        return 0xffffff
    }
}

const getOpacity = (node) => {
    const { graphical } = node

    if (graphical) {
        return 0.25
    } else {
        return 1
    }
}

const drawPath = (g, d, node) => {
    g.clear()
    const color = getColor(node)
    const opacity = getOpacity(node)
    g.lineStyle(2, color, opacity)
    let subpathStart = null

    const instructions = convertPathToInstructions(
        d.replace(/(?<![eE])-/g, ' -')
    )

    instructions?.forEach(
        ({
            type,
            points,
            point,
            rx,
            ry,
            xAxisRotation,
            largeArcFlag,
            sweepFlag,
        }) => {
            if (type === 'move') {
                g.moveTo(point[0], point[1])
                subpathStart = [point[0], point[1]]
            } else if (type === 'line') {
                g.lineTo(point[0], point[1])
            } else if (type === 'quadratic') {
                if (!points || points.length !== 2) return
                g.moveTo(points[0][0], points[0][1])
                g.quadraticCurveTo(
                    points[1][0],
                    points[1][1],
                    points[2][0],
                    points[2][1]
                )
            } else if (type === 'bezier') {
                if (!points || points.length !== 3) return
                g.bezierCurveTo(
                    points[0][0],
                    points[0][1],
                    points[1][0],
                    points[1][1],
                    points[2][0],
                    points[2][1]
                )
            } else if (type === 'arc') {
                // Unimplemented
            } else if (type === 'close') {
                if (subpathStart) {
                    g.lineTo(subpathStart[0], subpathStart[1])
                }
            }
        }
    )
    // g.endFill()
}

const drawEllipticalRoundedRect = (g, x, y, width, height, rx, ry, node) => {
    g.clear()
    const color = getColor(node)
    const opacity = getOpacity(node)
    g.lineStyle(2, color, opacity)
    g.beginFill(0x000000)

    // Ensure rx and ry are numbers and do not exceed half width/height
    const _rx = Math.min(Number(rx) || 0, width / 2)
    const _ry = Math.min(Number(ry) || 0, height / 2)

    if (!_rx && !_ry) {
        g.drawRect(x, y, width, height)
        return
    }

    // Start at top-left corner, after the horizontal radius
    g.moveTo(x + _rx, y)

    // Top edge
    g.lineTo(x + width - _rx, y)

    // Top-right corner (elliptical arc)
    g.arc(x + width - _rx, y + _ry, _rx, Math.PI * 1.5, 0, false)

    // Right edge
    g.lineTo(x + width, y + height - _ry)

    // Bottom-right corner (elliptical arc)
    g.arc(x + width - _rx, y + height - _ry, _rx, 0, Math.PI * 0.5, false)

    // Bottom edge
    g.lineTo(x + _rx, y + height)

    // Bottom-left corner (elliptical arc)
    g.arc(x + _rx, y + height - _ry, _rx, Math.PI * 0.5, Math.PI, false)

    // Left edge
    g.lineTo(x, y + _ry)

    // Top-left corner (elliptical arc)
    g.arc(x + _rx, y + _ry, _rx, Math.PI, Math.PI * 1.5, false)
    g.endFill()
}

const renderModelTree = (modelTree, path = 'root') => {
    if (!modelTree) return null

    let graphicsToDraw = []

    const {
        type,
        d,
        children,
        transform,
        r,
        cx,
        cy,
        x,
        y,
        rx,
        ry,
        width,
        height,
        href, // for images
    } = modelTree

    if (type === 'path') {
        graphicsToDraw.push(
            <Graphics
                key={`${path}-path`}
                draw={(g) => drawPath(g, d, modelTree)}
                interactive={true}
            />
        )
    } else if (type === 'circle') {
        graphicsToDraw.push(
            <Graphics
                key={`${path}-circle`}
                draw={(g) => {
                    g.clear()
                    const color = getColor(modelTree)
                    const opacity = getOpacity(modelTree)
                    g.lineStyle(2, color, opacity)
                    g.beginFill(0x000000)
                    g.drawCircle(cx, cy, r)
                    g.endFill()
                }}
                interactive={true}
            />
        )
    } else if (type === 'ellipse') {
        graphicsToDraw.push(
            <Graphics
                key={`${path}-ellipse`}
                draw={(g) => {
                    g.clear()
                    const color = getColor(modelTree)
                    const opacity = getOpacity(modelTree)
                    g.lineStyle(2, color, opacity)
                    g.beginFill(0x000000)
                    // PixiJS ellipse: drawEllipse(x, y, width, height)
                    // SVG ellipse: cx, cy, rx, ry
                    g.drawEllipse(cx, cy, rx, ry)
                    g.endFill()
                }}
                interactive={true}
            />
        )
    } else if (type === 'rectangle') {
        graphicsToDraw.push(
            <Graphics
                key={`${path}-rectangle`}
                draw={(g) => {
                    drawEllipticalRoundedRect(
                        g,
                        x,
                        y,
                        width,
                        height,
                        rx,
                        ry,
                        modelTree
                    )
                }}
                interactive={true}
            />
        )
    } else if (type === 'polyline') {
        graphicsToDraw.push(
            <Graphics
                key={`${path}-polyline`}
                draw={(g) => {
                    g.clear()
                    const color = getColor(modelTree)
                    const opacity = getOpacity(modelTree)
                    g.lineStyle(2, color, opacity)
                    const pts = modelTree.points
                    if (pts && pts.length > 0) {
                        g.moveTo(pts[0][0], pts[0][1])
                        for (let i = 1; i < pts.length; i++) {
                            g.lineTo(pts[i][0], pts[i][1])
                        }
                    }
                }}
                interactive={true}
            />
        )
    } else if (type === 'polygon') {
        graphicsToDraw.push(
            <Graphics
                key={`${path}-polygon`}
                draw={(g) => {
                    g.clear()
                    const color = getColor(modelTree)
                    g.lineStyle(2, color, 1)
                    const pts = modelTree.points
                    if (pts && pts.length > 0) {
                        g.moveTo(pts[0][0], pts[0][1])
                        for (let i = 1; i < pts.length; i++) {
                            g.lineTo(pts[i][0], pts[i][1])
                        }
                        g.lineTo(pts[0][0], pts[0][1]) // Close the shape
                    }
                }}
                interactive={true}
            />
        )
    } else if (type === 'image' && href) {
        graphicsToDraw.push(
            <Sprite
                key={`${path}-image`}
                image={href}
                x={x || 0}
                y={y || 0}
                width={width}
                height={height}
                anchor={0}
            />
        )
    }

    if (children) {
        children.forEach((child, idx) => {
            graphicsToDraw = [
                ...graphicsToDraw,
                renderModelTree(child, `${path}.${idx}`),
            ]
        })
    }

    return (
        <Container
            x={transform?.translate?.x || 0}
            y={transform?.translate?.y || 0}
            scale={{ x: transform?.scale?.x || 1, y: transform?.scale?.y || 1 }}
            key={`${path}-container`}
        >
            {graphicsToDraw}
        </Container>
    )
}

const ComplexPart = ({
    part,
    scale,
    parent,
    selectedPartId,
    hoveredPartId,
    onClick,
    onHoverPart,
    onClickPart,
}) => {
    const { partTable } = usePartTable()
    const [showAnchors] = useAtom(renderAnchorsAtom)
    const containerRef = useRef(null)
    const [forceRender, setForceRender] = useState(0)

    // Force a re-render after initial mount to fix sizing issues
    // TODO: This is a workaround for a PIXI Stage initialization timing issue
    // The real problem: scale isn't applied correctly on first render when Stage is still initializing
    useEffect(() => {
        const timer = setTimeout(() => {
            setForceRender((prev) => prev + 1)
        }, 500) // Increased delay for slower network/remote environments

        return () => clearTimeout(timer)
    }, []) // Only run once on mount

    // Early return if no part
    if (!part) {
        return null
    }

    let partToRender = { ...part, type: 'user' }
    if (!part.modelTree) {
        partToRender = { ...partTable['user'][part.partId], type: 'user' }
    }

    const [width, height] = calculateSizeOfPart(partToRender, partTable)

    let [fixedX, fixedY] = [part.position?.[0] || 0, part.position?.[1] || 0]
    if (parent) {
        const { parts, panelDimensions } = parent
        const [panelWidth, panelHeight] = panelDimensions || [0, 0]
        // Use absolutePosition if available, otherwise calculate it
        ;[fixedX, fixedY] =
            part.absolutePosition ||
            calculateRelativePosition(
                { ...part, dimensions: [width, height] },
                parts,
                panelWidth,
                panelHeight
            )
    }

    return (
        <Container
            ref={containerRef}
            key={`${part.id}-${forceRender}`}
            x={fixedX * scale}
            y={fixedY * scale}
            angle={part.rotation || 0}
        >
            <Graphics
                alpha={0}
                onclick={() => onClick && onClick(part)}
                onmouseover={() => onHoverPart && onHoverPart(part)}
                onmouseout={() => onHoverPart && onHoverPart(null)}
                onpointerdown={() => {
                    onClick && onClick(part)
                    onClickPart && onClickPart(part, 'DOWN')
                }}
                onpointerup={() => {
                    onClick && onClick(null)
                    onClickPart && onClickPart(part, 'UP')
                }}
                interactive={true}
                draw={(g) => {
                    g.clear()
                    g.beginFill('green')
                    g.lineStyle({ width: 2, color: 'green' })
                    g.drawRoundedRect(0, 0, width * scale, height * scale, 0)
                    g.endFill()
                }}
            />
            {selectedPartId === part.id || hoveredPartId === part.id ? (
                <Graphics
                    draw={(g) => {
                        g.clear()
                        g.lineStyle({ width: 2, color: 'green' })
                        g.drawRoundedRect(
                            0,
                            0,
                            width * scale,
                            height * scale,
                            0
                        )
                        g.endFill()
                    }}
                />
            ) : null}
            {showAnchors && part.anchor ? (
                <Graphics
                    draw={(g) => {
                        g.clear()

                        // Calculate anchor position within the complex part bounds
                        const anchorX = part.anchor[0] * width
                        const anchorY = part.anchor[1] * height

                        const crossSize = Math.max(3, 6 / scale) // Cross size that scales appropriately
                        const lineWidth = Math.max(1, 2 / scale)

                        // Draw red cross at anchor point
                        g.lineStyle(lineWidth, 0xff0000, 1) // Red color

                        // Horizontal line
                        g.moveTo(scale * (anchorX - crossSize), scale * anchorY)
                        g.lineTo(scale * (anchorX + crossSize), scale * anchorY)

                        // Vertical line
                        g.moveTo(scale * anchorX, scale * (anchorY - crossSize))
                        g.lineTo(scale * anchorX, scale * (anchorY + crossSize))
                    }}
                    zIndex={1000} // Above other elements
                />
            ) : null}
            <Container
                scale={[
                    scale * (part.flipX ? -1 : 1),
                    scale * (part.flipY ? -1 : 1),
                ]}
                x={part.flipX ? width * scale : 0}
                y={part.flipY ? height * scale : 0}
            >
                {renderModelTree(partToRender?.modelTree)}
            </Container>
        </Container>
    )
}

export default ComplexPart
